<template>
  <div class="exam-registration">
    <NoticeBar :visible="noticeVisible" :message="noticeMessage" :type="noticeType" :actions="noticeActions"
      @close="noticeVisible = false" />
    <!-- 报名表单视图 -->
    <div v-if="layout === 'sheet'" class="sheet-form">
      <h1 class="sheet-title">{{ exam.title || '考试报名表' }}</h1>
      <p v-if="showRegisterTime" class="sheet-subtitle">{{ exam.enroll_start_time }} 至 {{ exam.enroll_end_time }}</p>

      <!-- 预填充提示 -->
      <div v-if="prefillNote" class="prefill-note" :class="{ 'reject-note': prefillNote.includes('驳回') }">
        {{ prefillNote }}
      </div>

      <!-- 基本信息表格 -->
      <table class="sheet-table">
        <tbody>
          <!-- 姓名 -->
          <tr>
            <td class="th" rowspan="4">基本信息</td>
            <td>姓名</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.realName" type="text" placeholder="请输入真实姓名" @input="startEdit">
              <span v-else>{{ form.realName || '-' }}</span>
            </td>
          </tr>
          <!-- 性别 -->
          <tr>
            <td>性别</td>
            <td colspan="3">
              <select v-if="editing" v-model="form.gender" @change="startEdit">
                <option value="">请选择性别</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
              <span v-else>{{ form.gender || '-' }}</span>
            </td>
          </tr>
          <!-- 年龄 -->
          <tr>
            <td>年龄</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.age" type="number" placeholder="请输入年龄" min="18" max="65"
                @input="startEdit">
              <span v-else>{{ form.age || '-' }}</span>
            </td>
          </tr>
          <!-- 手机号 -->
          <tr>
            <td>手机号</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.phone" type="tel" placeholder="请输入手机号码" maxlength="11"
                @input="normalizePhone">
              <span v-else>{{ form.phone || '-' }}</span>
            </td>
          </tr>
          <!-- 邮箱 -->
          <tr>
            <td class="th" rowspan="2">联系信息</td>
            <td>邮箱</td>
            <td colspan="3">
              <div v-if="editing" class="email-input-wrapper">
                <input v-model="form.email" type="text" placeholder="请输入邮箱地址" class="email-input" @input="onEmailInput">
                <div class="email-quick-btns">
                  <button type="button" v-for="domain in quickDomains" :key="domain" 
                    class="quick-domain-btn" @click="appendDomain(domain)">@{{ domain }}</button>
                </div>
              </div>
              <span v-else>{{ form.email || '-' }}</span>
            </td>
          </tr>
          <!-- 身份证号 -->
          <tr>
            <td>身份证号</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.idNumber" type="text" placeholder="请输入18位身份证号" maxlength="18"
                @input="normalizeIdNumber">
              <span v-else>{{ form.idNumber || '-' }}</span>
            </td>
          </tr>
          <!-- 报考岗位 -->
          <tr>
            <td class="th" rowspan="3">报考信息</td>
            <td>报考岗位</td>
            <td colspan="3">
              <select v-if="editing" v-model="form.position" @change="startEdit">
                <option value="">请选择报考岗位</option>
                <option v-for="pos in exam.positions" :key="pos" :value="pos">{{ pos }}</option>
              </select>
              <span v-else>{{ form.position || '-' }}</span>
            </td>
          </tr>
          <!-- 工作单位 -->
          <tr>
            <td>工作单位</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.company" type="text" placeholder="请输入工作单位" @input="startEdit">
              <span v-else>{{ form.company || '-' }}</span>
            </td>
          </tr>
          <!-- 工作经历 -->
          <tr>
            <td>工作经历</td>
            <td colspan="3">
              <textarea v-if="editing" v-model="form.workExperience" placeholder="请输入工作经历，每行一条" rows="4"
                @input="startEdit"></textarea>
              <div v-else class="work-experience-display">
                <div v-if="workExperienceLines.length">
                  <div v-for="(line, i) in workExperienceLines" :key="i" class="experience-line">• {{ line }}</div>
                </div>
                <span v-else>-</span>
              </div>
            </td>
          </tr>
          <!-- 学历 -->
          <tr>
            <td class="th" rowspan="4">教育信息</td>
            <td>学历</td>
            <td colspan="3">
              <select v-if="editing" v-model="form.education" @change="startEdit">
                <option value="">请选择学历</option>
                <option value="小学">小学</option>
                <option value="初中">初中</option>
                <option value="高中">高中</option>
                <option value="中专">中专</option>
                <option value="大专">大专</option>
                <option value="本科">本科</option>
                <option value="硕士">硕士</option>
                <option value="博士">博士</option>
              </select>
              <span v-else>{{ form.education || '-' }}</span>
            </td>
          </tr>
          <!-- 毕业院校 -->
          <tr>
            <td>毕业院校</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.school" type="text" placeholder="请输入毕业院校" @input="startEdit">
              <span v-else>{{ form.school || '-' }}</span>
            </td>
          </tr>
          <!-- 专业 -->
          <tr>
            <td>专业</td>
            <td colspan="3">
              <input v-if="editing" v-model="form.major" type="text" placeholder="请输入专业" @input="startEdit">
              <span v-else>{{ form.major || '-' }}</span>
            </td>
          </tr>
          <!-- 学位 -->
          <tr>
            <td>学位</td>
            <td colspan="3">
              <select v-if="editing" v-model="form.degree" @change="startEdit">
                <option value="">请选择学位</option>
                <option value="无">无</option>
                <option value="学士">学士</option>
                <option value="硕士">硕士</option>
                <option value="博士">博士</option>
              </select>
              <span v-else>{{ form.degree || '-' }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 附件上传区域 -->
      <h3 class="upload-title">附件材料</h3>
      <table class="sheet-table attachment-table">
        <tbody>
          <!-- 个人照片 -->
          <tr>
            <td class="th" rowspan="4">证件照片</td>
            <td>个人照片</td>
            <td colspan="3" class="photo-wrap">
              <div v-if="editing" class="upload-card" @click="triggerPersonalPhotoUpload">
                <input ref="personalPhotoInput" type="file" accept="image/*" @change="onPersonalPhotoSelected"
                  style="display: none;">
                <div v-if="personalPhotoPreview" class="preview-image">
                  <img :src="personalPhotoPreview" alt="个人照片预览">
                  <button class="remove-btn" @click.stop="personalPhoto = null; personalPhotoPreview = ''">×</button>
                </div>
                <div v-else-if="existingPersonalPhoto" class="existing-image">
                  <SecureImage :attachment="existingPersonalPhoto" alt="已上传的个人照片" />
                  <button class="remove-btn" @click.stop="existingPersonalPhoto = null">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <span class="upload-icon">+</span>
                  <span class="upload-text">点击上传个人照片</span>
                </div>
              </div>
              <div v-else class="view-image">
                <SecureImage v-if="existingPersonalPhoto" :attachment="existingPersonalPhoto" alt="个人照片" />
                <span v-else>-</span>
              </div>
            </td>
          </tr>
          <!-- 身份证正面 -->
          <tr>
            <td>身份证正面</td>
            <td colspan="3" class="photo-wrap">
              <div v-if="editing" class="upload-card" @click="triggerIdFrontUpload">
                <input ref="idFrontInput" type="file" accept="image/*" @change="onIdFrontSelected"
                  style="display: none;">
                <div v-if="idCardFrontPreview" class="preview-image">
                  <img :src="idCardFrontPreview" alt="身份证正面预览">
                  <button class="remove-btn" @click.stop="idCardFront = null; idCardFrontPreview = ''">×</button>
                </div>
                <div v-else-if="existingIdFront" class="existing-image">
                  <SecureImage :attachment="existingIdFront" alt="已上传的身份证正面" />
                  <button class="remove-btn" @click.stop="existingIdFront = null">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <span class="upload-icon">+</span>
                  <span class="upload-text">点击上传身份证正面</span>
                </div>
              </div>
              <div v-else class="view-image">
                <SecureImage v-if="existingIdFront" :attachment="existingIdFront" alt="身份证正面" />
                <span v-else>-</span>
              </div>
            </td>
          </tr>
          <!-- 身份证反面 -->
          <tr>
            <td>身份证反面</td>
            <td colspan="3" class="photo-wrap">
              <div v-if="editing" class="upload-card" @click="triggerIdBackUpload">
                <input ref="idBackInput" type="file" accept="image/*" @change="onIdBackSelected" style="display: none;">
                <div v-if="idCardBackPreview" class="preview-image">
                  <img :src="idCardBackPreview" alt="身份证反面预览">
                  <button class="remove-btn" @click.stop="idCardBack = null; idCardBackPreview = ''">×</button>
                </div>
                <div v-else-if="existingIdBack" class="existing-image">
                  <SecureImage :attachment="existingIdBack" alt="已上传的身份证反面" />
                  <button class="remove-btn" @click.stop="existingIdBack = null">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <span class="upload-icon">+</span>
                  <span class="upload-text">点击上传身份证反面</span>
                </div>
              </div>
              <div v-else class="view-image">
                <SecureImage v-if="existingIdBack" :attachment="existingIdBack" alt="身份证反面" />
                <span v-else>-</span>
              </div>
            </td>
          </tr>
          <!-- 毕业证书 -->
          <tr>
            <td>毕业证书</td>
            <td colspan="3" class="photo-wrap">
              <div v-if="editing" class="upload-card" @click="triggerGraduationUpload">
                <input ref="graduationInput" type="file" accept="image/*" @change="onGraduationSelected"
                  style="display: none;">
                <div v-if="graduationCertPreview" class="preview-image">
                  <img :src="graduationCertPreview" alt="毕业证书预览">
                  <button class="remove-btn" @click.stop="graduationCert = null; graduationCertPreview = ''">×</button>
                </div>
                <div v-else-if="existingGraduationCert" class="existing-image">
                  <SecureImage :attachment="existingGraduationCert" alt="已上传的毕业证书" />
                  <button class="remove-btn" @click.stop="existingGraduationCert = null">×</button>
                </div>
                <div v-else class="upload-placeholder">
                  <span class="upload-icon">+</span>
                  <span class="upload-text">点击上传毕业证书</span>
                </div>
              </div>
              <div v-else class="view-image">
                <SecureImage v-if="existingGraduationCert" :attachment="existingGraduationCert" alt="毕业证书" />
                <span v-else>-</span>
              </div>
            </td>
          </tr>
          <!-- 其他附件 -->
          <tr>
            <td class="th">其他附件</td>
            <td colspan="4">
              <div v-if="editing" class="other-attachments">
                <div v-for="(file, i) in attachments" :key="i" class="attachment-item">
                  <span class="file-name">{{ file.name }}</span>
                  <button class="remove-btn" @click="removeFile(i)">×</button>
                </div>
                <div v-for="(file, i) in existingAttachments" :key="'existing-' + i" class="attachment-item existing">
                  <span class="file-name">{{ file.original_name }}</span>
                  <button class="remove-btn" @click="existingAttachments.splice(i, 1)">×</button>
                </div>
                <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" @change="onFilesSelected">
              </div>
              <div v-else class="view-attachments">
                <div v-for="(file, i) in existingAttachments" :key="i" class="attachment-item">
                  <span class="file-name">{{ file.original_name }}</span>
                  <button class="download-btn" @click="downloadExistingAttachment(file)">下载</button>
                </div>
                <span v-if="existingAttachments.length === 0">-</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 操作按钮 -->
      <div v-if="editing && editable" class="form-actions">
        <button class="btn-action btn-primary" @click="handleSubmit" :disabled="submitting">
          {{ submitting ? '提交中...' : submitButtonText }}
        </button>
        <button v-if="!submission" class="btn-action btn-secondary" @click="resetForm">重置</button>
      </div>
    </div>

    <!-- 准考证视图（仅发布后显示，或 layout 为 ticket） -->
    <div v-else-if="layout === 'ticket' || (ticket && (ticket.is_published === true || ticket.published === true))" class="ticket-view">
      <!-- 准考证标题区域 -->
      <div class="ticket-header">
        <h1 class="ticket-title">准考证</h1>
        <h2 class="company-name">{{ ticket?.company_name || exam?.ticket_company_name || '考试单位' }}</h2>
        <h3 class="exam-year">{{ exam?.title || ticket?.exam_title || '考试' }}</h3>
      </div>

      <!-- 个人信息区域 -->
      <div class="personal-info">
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">考生姓名：</span>
            <span class="info-value">{{ form.realName || ticket?.submission_info?.real_name || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">身份证号：</span>
            <span class="info-value">{{ form.idNumber || ticket?.submission_info?.id_number || '-' }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">报考岗位：</span>
            <span class="info-value">{{ form.position || ticket?.position || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">准考证号：</span>
            <span class="info-value">{{ displayTicketNumber }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item full-width">
            <span class="info-label">笔试时间：</span>
            <span class="info-value">{{ ticket?.exam_time_text || '2025年1月4日（星期五）上午8:30-10:30' }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item full-width">
            <span class="info-label">笔试地点：</span>
            <span class="info-value">{{ ticket?.venue || '鄂尔多斯理工学校（鄂尔多斯市东胜区世纪大道鄂尔多斯理工学校）' }}</span>
          </div>
        </div>
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">考场号：</span>
            <span class="info-value">{{ displayRoom }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">座位号：</span>
            <span class="info-value">{{ ticket?.seat || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- 提示信息 -->
      <div class="ticket-reminder">
        <p>考生应持准考证和身份证进入考场</p>
        <p>请考生在考试前务必阅读考生须知</p>
      </div>

      <!-- 考生须知部分 -->
      <div class="examinee-notice">
        <h3>考生须知</h3>
        <ol v-if="ticketNotesList.length > 0">
          <li v-for="(note, idx) in ticketNotesList" :key="idx">{{ note }}</li>
        </ol>
        <ol v-else>
          <li>考试前30分钟，考生凭身份证、准考证进入考场，将准考证和身份证放于桌面左上角；考试开始30分钟后，考生不得进入考场参加考试；</li>
          <li>考生须将背包、书籍、笔记、报刊、手机（设置为关机状态）、耳机、电子手表等放于考场外指定地点，只准带准考证、身份证及文具进入考场。考试开始后不许相互借用文具及其他物品；</li>
          <li>考生发现试卷分发错误、试卷字迹模糊、有折皱和污点等，可举手询问；</li>
          <li>考生要保持考场安静，不许交头接耳、左顾右盼，禁止偷看他人试题答案或交换试卷、草稿纸等；考场内禁止吸烟；</li>
          <li>考生需自备填涂答题卡所需的2B铅笔、橡皮、碳素笔；</li>
          <li>考生应当尊重考场工作人员，自觉接受监考人员的监督和检查；</li>
          <li>考试结束信号发出后，考生应当立即停止答卷，将试题、答题纸和草稿纸反扣在桌面上，经监考人员允许后方可离开考场；考生不得将试卷、答题卡、草稿纸及一切纸质资料带出考场；</li>
          <li>考生因个人原因坐错座位、姓名、准考证号填写错误、涂抹等造成差错的，后果自负；</li>
          <li>考试时间120分钟，不能提前交卷；</li>
          <li>考场内外不得大声喧哗，禁止吸烟。</li>
        </ol>
      </div>

      <!-- 打印按钮 -->
      <div class="ticket-actions">
        <button class="btn-action btn-primary" @click="startPrint">打印准考证</button>
        <button class="btn-action btn-secondary" @click="saveAsImage">保存为图片</button>
      </div>
    </div>

    <!-- 默认空状态 -->
    <div v-else class="empty-state">
      <p>请选择考试进行报名</p>
    </div>
  </div>
</template>

<script>
import { getAccessToken } from "../api/storage"
import SecureImage from './SecureImage.vue'
import NoticeBar from './NoticeBar.vue'
export default {
  name: 'ExamRegistrationForm',
  props: {
    exam: { type: Object, default: () => ({}) },
    submission: { type: Object, default: null },
    ticket: { type: Object, default: null },
    editable: { type: Boolean, default: true },
    layout: { type: String, default: 'form' },
    actionsMode: { type: String, default: 'full' },
    // 是否展示“报名时间”列（与示例保持可选）
    showRegisterTime: { type: Boolean, default: false },
    // 是否展示状态与原因（示例为红色提示，可选）
    showStatus: { type: Boolean, default: false }
  },
  components: { SecureImage, NoticeBar },

  data() {
    return {
      form: {
        realName: '',
        age: '',
        phone: '',
        email: '',
        idNumber: '',
        gender: '',
        ethnicity: '',
        political: '',
        birthDate: '',
        education: '',
        degree: '',
        school: '',
        major: '',
        company: '',
        workExperience: '',
        position: ''
      },
      // 常用邮箱域名快捷按钮
      quickDomains: ['qq.com', '163.com', 'gmail.com'],
      personalPhoto: null,
      personalPhotoPreview: '',
      idCardFront: null,
      idCardBack: null,
      idCardFrontPreview: '',
      idCardBackPreview: '',
      graduationCert: null,
      graduationCertPreview: '',
      attachments: [],
      // 已上传附件（上次被驳回后用于保留）
      existingPersonalPhoto: null,
      existingIdFront: null,
      existingIdBack: null,
      existingGraduationCert: null,
      existingAttachments: [],
      submitting: false,
      prefilled: false,
      prefillNote: '',
      editing: this.layout === 'sheet' ? (!this.submission ? true : false) : true,
      noticeVisible: false,
      noticeMessage: '',
      noticeType: 'info',
      noticeActions: []
    }
  },
  computed: {
    displayRoom() {
      const ex = this.exam || {}
      const tk = this.ticket || {}
      const room = (ex.ticket_room || tk.room || '').toString().trim()
      return room || '-'
    },
    displayTicketNumber() {
      const tk = this.ticket || {}
      const n = (tk.ticket_number || '').toString().trim()
      if (!n) return '-'
      // 格式：YYMMDD(6位) + 职位号(2位) + 考场号(2位) + 座位号(2位) + 考试ID(2位)
      // 展示时去掉最后2位的考试ID
      if (/^\d+$/.test(n) && n.length >= 14) {
        return n.slice(0, -2)  // 去掉最后2位考试ID
      }
      return n
    },
    ticketNotesList() {
      const notes = this.ticket?.notes || this.exam?.ticket_notes || ''
      if (!notes) return []
      return notes.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
    }
  },
  async mounted() {
    // 确保所有表单字段都不是 null
    this.sanitizeFormFields()
    if (this.submission) {
      this.prefillFromSubmission(this.submission)
    } else {
      await this.tryPrefillFromPrevious()
    }
    // 再次确保没有 null 值
    this.sanitizeFormFields()
  },
  computed: {
    submitButtonText() {
      const s = this.submission
      const audit = s?.audit_status || s?.status || null
      // 首次报名：无 submission 记录
      if (!s) return '提交报名'
      // 被驳回：文案为重新提交
      if (audit === 'rejected') return '重新提交'
      // 其他情况（审核中/通过等）：默认仍显示提交报名
      return '提交报名'
    },
    canPrintAdmission() {
      const s = this.submission
      const audit = s?.audit_status || s?.status || null
      const pay = s?.payment_status || null
      return audit === 'approved' && pay === 'paid'
    },
    ageDisplay() {
      // 优先从身份证号解析出生日期（18位，YYYYMMDD）
      const id = String(this.form.idNumber || '').trim()
      if (/^\d{17}[\dX]$/i.test(id)) {
        const y = id.slice(6, 10), m = id.slice(10, 12), d = id.slice(12, 14)
        const dt = new Date(`${y}-${m}-${d}`)
        if (!isNaN(dt.getTime())) {
          const now = new Date()
          let age = now.getFullYear() - dt.getFullYear()
          const m1 = now.getMonth(), m2 = dt.getMonth()
          if (m1 < m2 || (m1 === m2 && now.getDate() < dt.getDate())) age--
          return String(age)
        }
      }
      // 回退到手动填写的出生日期
      const d = String(this.form.birthDate || '').trim()
      if (!d) return '-'
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return '-'
      const now = new Date()
      let age = now.getFullYear() - dt.getFullYear()
      const m1 = now.getMonth(), m2 = dt.getMonth()
      if (m1 < m2 || (m1 === m2 && now.getDate() < dt.getDate())) age--
      return String(age)
    },
    workExperienceLines() {
      const raw = String(this.form.workExperience || '').trim()
      if (!raw) return []
      return raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
    }
  },
  methods: {
    // 确保所有表单字段都不是 null，避免显示 "null"
    sanitizeFormFields() {
      const f = this.form
      Object.keys(f).forEach(key => {
        if (f[key] === null || f[key] === undefined || f[key] === 'null') {
          f[key] = ''
        }
      })
    },
    resolveUrl(path) {
      if (!path) return ''
      const s = String(path)
      const m = s.match(/\/media\/.*$/)
      if (m) return m[0]
      if (s.startsWith('http://') || s.startsWith('https://')) return s
      const normalized = s.startsWith('/') ? s : `/${s}`
      if (normalized.startsWith('/media') || normalized.startsWith('/api')) return normalized
      const backendBase = `${window.location.protocol}//${window.location.hostname}:8000`
      return `${backendBase}${normalized}`
    },
    async downloadExistingAttachment(file) {
      try {
        const id = file && file.id
        if (!id) return
        const token = getAccessToken() || localStorage.getItem('user_access_token') || ''
        const res = await fetch(`/api/exam/applications/attachments/${id}/download/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (!res.ok) throw new Error('下载接口返回错误')
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.original_name || '附件'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.noticeMessage = '下载失败，请稍后重试'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 2500)
      }
    },
    // 邮箱输入处理
    onEmailInput() {
      // 去除空格
      this.form.email = String(this.form.email || '').replace(/\s+/g, '')
    },
    // 快捷添加域名
    appendDomain(domain) {
      const current = String(this.form.email || '').trim()
      // 如果已有@，替换域名部分
      if (current.includes('@')) {
        this.form.email = current.split('@')[0] + '@' + domain
      } else {
        // 否则直接追加
        this.form.email = current + '@' + domain
      }
    },
    startEdit() { if (this.editable) this.editing = true },
    startPrint() {
      const prev = this.editing
      this.editing = false
      this.$nextTick(() => {
        window.print()
        // 恢复原编辑状态
        this.editing = prev
      })
    },
    async saveAsImage() {
      const prev = this.editing
      try {
        // 切换到只读态以获得与打印一致的视觉效果
        this.editing = false
        await this.$nextTick()
        const target = this.$el.querySelector('.sheet-form') || this.$el

        // 动态加载 html2canvas（若项目未安装依赖）
        const ensureHtml2Canvas = () => new Promise((resolve, reject) => {
          if (window.html2canvas) return resolve(window.html2canvas)
          const script = document.createElement('script')
          script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'
          script.async = true
          script.onload = () => resolve(window.html2canvas)
          script.onerror = () => reject(new Error('html2canvas 加载失败'))
          document.head.appendChild(script)
        })

        const h2c = await ensureHtml2Canvas()
        if (!h2c || !target) throw new Error('无法定位截图区域')

        const canvas = await h2c(target, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
        // 生成 PNG 并下载
        const fileName = `${(this.exam && (this.exam.title || this.exam.name)) || '准考证'}.png`
        if (canvas.toBlob) {
          canvas.toBlob((blob) => {
            if (!blob) { this.noticeMessage = '生成图片失败'; this.noticeType = 'error'; this.noticeActions = []; this.noticeVisible = true; setTimeout(() => { this.noticeVisible = false }, 2500); return }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
          })
        } else {
          const url = canvas.toDataURL('image/png')
          const a = document.createElement('a')
          a.href = url
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      } catch (err) {
        console.warn('保存为图片失败', err)
        this.noticeMessage = '保存图片失败，可尝试使用浏览器截图。'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 2500)
      } finally {
        // 恢复原编辑状态
        this.editing = prev
      }
    },
    mapAuditStatus(s) {
      switch (s) {
        case 'approved': return '审核已通过'
        case 'rejected': return '审核未通过'
        case 'pending': return '待审核'
        default: return s || '-'
      }
    },
    normalizePhone() {
      // 仅保留数字，限制 11 位
      const digits = String(this.form.phone || '').replace(/\D/g, '').slice(0, 11)
      this.form.phone = digits
    },
    normalizeIdNumber() {
      // 仅保留数字与X，限制为 18 位，并统一大写
      const v = String(this.form.idNumber || '').toUpperCase().replace(/[^0-9X]/gi, '').slice(0, 18)
      this.form.idNumber = v
      // 自动计算年龄
      this.calculateAgeFromIdNumber()
    },
    normalizeBirthDate() {
      const raw = String(this.form.birthDate || '')
      const digits = raw.replace(/\D/g, '').slice(0, 8)
      if (digits.length <= 4) {
        this.form.birthDate = digits
        return
      }
      if (digits.length <= 6) {
        this.form.birthDate = `${digits.slice(0, 4)}-${digits.slice(4, 6)}`
        return
      }
      this.form.birthDate = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`
    },
    // 根据身份证号计算年龄
    calculateAgeFromIdNumber() {
      const idNumber = String(this.form.idNumber || '').trim()
      if (!/^\d{17}[\dXx]$/.test(idNumber)) {
        return // 无效身份证号，不计算年龄
      }

      // 从身份证号中提取出生日期（YYYYMMDD）
      const birthDateStr = idNumber.slice(6, 14)
      const year = parseInt(birthDateStr.slice(0, 4))
      const month = parseInt(birthDateStr.slice(4, 6))
      const day = parseInt(birthDateStr.slice(6, 8))

      const birthDate = new Date(year, month - 1, day)
      const today = new Date()

      // 计算年龄
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      const dayDiff = today.getDate() - birthDate.getDate()

      // 如果生日还没过，年龄减1
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--
      }

      this.form.age = age
    },
    handleCellClick(e) {
      try {
        if (!this.editing) return
        const t = e.target
        // 若已在输入控件上则无需处理
        if (t && ['INPUT', 'SELECT', 'TEXTAREA'].includes(t.tagName)) return
        const td = t && typeof t.closest === 'function' ? t.closest('td') : null
        if (!td) return
        const focusable = td.querySelector('input[type="text"], input[type="tel"], input[type="email"], textarea, select')
        if (focusable && typeof focusable.focus === 'function') focusable.focus()
      } catch (_) { /* 静默 */ }
    },
    triggerPersonalPhotoUpload() {
      // 仅在可编辑状态下允许点击重新上传
      if (!this.editing || !this.editable) return
      const input = this.$refs.personalPhotoInput
      if (input && typeof input.click === 'function') {
        input.click()
      }
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files || [])
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']
      const validFiles = []
      const invalidFiles = []
      
      files.forEach(f => {
        const fileName = f.name.toLowerCase()
        const isValidType = allowedTypes.some(type => fileName.endsWith(type))
        const isValidSize = f.size <= maxSize
        
        if (isValidType && isValidSize) {
          validFiles.push(f)
        } else {
          invalidFiles.push({
            name: f.name,
            reason: !isValidType ? '文件类型不支持' : '文件大小超过10MB'
          })
        }
      })
      
      this.attachments = validFiles
      
      if (invalidFiles.length > 0) {
        const messages = invalidFiles.map(f => `${f.name}: ${f.reason}`)
        this.noticeMessage = `部分文件未添加：\n${messages.join('\n')}`
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 4000)
      }
    },
    onPersonalPhotoSelected(e) {
      const f = (e.target.files || [])[0]
      if (!f) return
      
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (f.size > maxSize) {
        this.noticeMessage = `文件大小超过10MB，请选择较小的图片`
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = '' // 清空input
        return
      }
      
      // 验证图片类型
      if (!f.type.startsWith('image/')) {
        this.noticeMessage = '请选择图片文件（JPG、PNG等）'
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      this.personalPhoto = f
      try {
        this.personalPhotoPreview = URL.createObjectURL(f)
      } catch (err) {
        this.personalPhotoPreview = ''
        this.noticeMessage = '预览图片失败'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
      }
    },
    onIdFrontSelected(e) {
      const f = (e.target.files || [])[0]
      if (!f) return
      
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (f.size > maxSize) {
        this.noticeMessage = `文件大小超过10MB，请选择较小的图片`
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      if (!f.type.startsWith('image/')) {
        this.noticeMessage = '请选择图片文件（JPG、PNG等）'
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      this.idCardFront = f
      try {
        this.idCardFrontPreview = URL.createObjectURL(f)
      } catch (err) {
        this.idCardFrontPreview = ''
        this.noticeMessage = '预览图片失败'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
      }
    },
    onIdBackSelected(e) {
      const f = (e.target.files || [])[0]
      if (!f) return
      
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (f.size > maxSize) {
        this.noticeMessage = `文件大小超过10MB，请选择较小的图片`
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      if (!f.type.startsWith('image/')) {
        this.noticeMessage = '请选择图片文件（JPG、PNG等）'
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      this.idCardBack = f
      try {
        this.idCardBackPreview = URL.createObjectURL(f)
      } catch (err) {
        this.idCardBackPreview = ''
        this.noticeMessage = '预览图片失败'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
      }
    },
    onGraduationSelected(e) {
      const f = (e.target.files || [])[0]
      if (!f) return
      
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (f.size > maxSize) {
        this.noticeMessage = `文件大小超过10MB，请选择较小的图片`
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      if (!f.type.startsWith('image/')) {
        this.noticeMessage = '请选择图片文件（JPG、PNG等）'
        this.noticeType = 'warning'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
        e.target.value = ''
        return
      }
      
      this.graduationCert = f
      try {
        this.graduationCertPreview = URL.createObjectURL(f)
      } catch (err) {
        this.graduationCertPreview = ''
        this.noticeMessage = '预览图片失败'
        this.noticeType = 'error'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 3000)
      }
    },
    triggerIdFrontUpload() { const el = this.$refs.idFrontInput; if (this.editing && this.editable && el && el.click) el.click() },
    triggerIdBackUpload() { const el = this.$refs.idBackInput; if (this.editing && this.editable && el && el.click) el.click() },
    triggerGraduationUpload() { const el = this.$refs.graduationInput; if (this.editing && this.editable && el && el.click) el.click() },
    removeFile(idx) {
      this.attachments.splice(idx, 1)
    },
    resetForm() {
      if (!confirm('确定要重置表单吗？所有已填写的内容将被清空。')) {
        return
      }
      this.form = {
        realName: '', age: '', phone: '', email: '', idNumber: '', gender: '', ethnicity: '', political: '',
        birthDate: '', education: '', degree: '', school: '', major: '', company: '', workExperience: '', position: ''
      }
      this.personalPhoto = null
      this.personalPhotoPreview = ''
      this.idCardFront = null
      this.idCardFrontPreview = ''
      this.idCardBack = null
      this.idCardBackPreview = ''
      this.graduationCert = null
      this.graduationCertPreview = ''
      this.attachments = []
      // 清理URL对象，避免内存泄漏
      if (this.personalPhotoPreview && this.personalPhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.personalPhotoPreview)
      }
      if (this.idCardFrontPreview && this.idCardFrontPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.idCardFrontPreview)
      }
      if (this.idCardBackPreview && this.idCardBackPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.idCardBackPreview)
      }
      if (this.graduationCertPreview && this.graduationCertPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.graduationCertPreview)
      }
      this.noticeMessage = '表单已重置'
      this.noticeType = 'info'
      this.noticeActions = []
      this.noticeVisible = true
      setTimeout(() => { this.noticeVisible = false }, 2000)
    },
    prefillFromSubmission(app) {
      try {
        if (!app) return
        const f = this.form
        f.realName = app?.real_name ?? ''
        f.phone = app?.phone ?? ''
        f.email = app?.email ?? ''
        f.idNumber = app?.id_number ?? ''
        f.age = app?.age ?? ''
        f.gender = app?.gender ?? ''
        f.ethnicity = app?.ethnicity ?? ''
        f.political = app?.political ?? ''
        f.birthDate = app?.birth_date ?? ''
        f.education = app?.education ?? ''
        f.degree = app?.degree ?? ''
        f.school = app?.school ?? ''
        f.major = app?.major ?? ''
        f.company = app?.company ?? ''
        f.workExperience = app?.work_experience ?? ''
        f.position = app?.position ?? ''

        const atts = Array.isArray(app?.attachments) ? app.attachments : []
        const pickByPrefix = (prefix) => atts.find(a => String(a?.original_name || '').startsWith(prefix)) || null
        this.existingPersonalPhoto = pickByPrefix('personal_photo_')
        this.existingIdFront = pickByPrefix('id_front_')
        this.existingIdBack = pickByPrefix('id_back_')
        this.existingGraduationCert = pickByPrefix('graduation_cert_')
        this.existingAttachments = atts.filter(a => {
          const name = String(a?.original_name || '')
          return !(
            name.startsWith('personal_photo_') ||
            name.startsWith('id_front_') ||
            name.startsWith('id_back_') ||
            name.startsWith('graduation_cert_')
          )
        })

        this.prefilled = true
        const audit = app?.status || app?.audit_status
        if (audit === 'approved') {
          this.prefillNote = '该报名已审核通过，资料仅可查看，不可修改'
        } else if (audit === 'rejected') {
          this.prefillNote = app?.reject_reason ? `已填充当前报名信息（驳回原因：${app.reject_reason}），可修改后重新提交。` : '已填充当前报名信息，可修改后重新提交。'
        } else {
          this.prefillNote = '已填充当前报名信息，审核通过前均可修改。'
        }
        // 确保没有 null 值
        this.sanitizeFormFields()
      } catch (e) {
        // 静默失败，不影响正常填写
      }
    },
    async tryPrefillFromPrevious() {
      try {
        const examId = this.exam?.id
        const token = getAccessToken() || localStorage.getItem('user_access_token') || ''
        if (!examId || !token) return
        const res = await fetch('/api/exam/applications/my/', { headers: { 'Authorization': `Bearer ${token}` } })
        if (!res.ok) return
        const list = await res.json().catch(() => [])
        if (!Array.isArray(list) || list.length === 0) return
        const app = list.find(x => String(x?.exam?.id || x?.exam_id || '') === String(examId))
        if (!app) return
        // 所有状态均自动预填，便于查看或修改
        const audit = app?.status || app?.audit_status

        const f = this.form
        f.realName = app?.real_name ?? ''
        f.phone = app?.phone ?? ''
        f.email = app?.email ?? ''
        f.idNumber = app?.id_number ?? ''
        f.age = app?.age ?? ''
        f.gender = app?.gender ?? ''
        f.ethnicity = app?.ethnicity ?? ''
        f.political = app?.political ?? ''
        f.birthDate = app?.birth_date ?? ''
        f.education = app?.education ?? ''
        f.degree = app?.degree ?? ''
        f.school = app?.school ?? ''
        f.major = app?.major ?? ''
        f.company = app?.company ?? ''
        f.workExperience = app?.work_experience ?? ''
        f.position = app?.position ?? ''

        // 处理并展示已上传附件（保留）
        const atts = Array.isArray(app?.attachments) ? app.attachments : []
        const pickByPrefix = (prefix) => atts.find(a => String(a?.original_name || '').startsWith(prefix)) || null
        this.existingPersonalPhoto = pickByPrefix('personal_photo_')
        this.existingIdFront = pickByPrefix('id_front_')
        this.existingIdBack = pickByPrefix('id_back_')
        this.existingGraduationCert = pickByPrefix('graduation_cert_')
        this.existingAttachments = atts.filter(a => {
          const name = String(a?.original_name || '')
          return !(
            name.startsWith('personal_photo_') ||
            name.startsWith('id_front_') ||
            name.startsWith('id_back_') ||
            name.startsWith('graduation_cert_')
          )
        })

        this.prefilled = true
        if (audit === 'approved') {
          this.prefillNote = '该报名已审核通过，资料仅可查看，不可修改'
          this.editing = false
        } else if (audit === 'rejected') {
          this.prefillNote = app?.reject_reason ? `已自动填充上次报名信息（驳回原因：${app.reject_reason}）。请修改后重新提交。` : '已自动填充上次报名信息，请修改后重新提交。'
        } else if (audit === 'pending') {
          this.prefillNote = '已自动填充当前报名信息，审核中也可修改并重新提交。'
        } else {
          this.prefillNote = '已自动填充当前报名信息，审核通过前均可修改。'
        }
        // 确保没有 null 值
        this.sanitizeFormFields()
      } catch (e) {
        // 静默失败，不影响正常填写
      }
    },
    async handleSubmit() {
      if (this.submitting) return
      try {
        // 基本校验：必填项
        const required = {
          realName: '真实姓名',
          phone: '手机号码',
          idNumber: '身份证号码',
          position: '报考岗位',
        }
        const missing = Object.entries(required).filter(([k]) => !String(this.form[k] || '').trim())
        if (missing.length) {
          this.noticeMessage = `请填写必填项：${missing.map(([, label]) => label).join('、')}`
          this.noticeType = 'warning'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }
        // 邮箱校验
        const emailValue = String(this.form.email || '').trim()
        if (!emailValue || !/.+@.+\..+/.test(emailValue)) {
          this.noticeMessage = '请输入有效的邮箱地址'
          this.noticeType = 'warning'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }
        // 手机号与身份证简单校验（与注册页保持一致风格）
        if (!/^1[3-9]\d{9}$/.test(String(this.form.phone))) {
          this.noticeMessage = '请输入有效的手机号码'
          this.noticeType = 'warning'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }
        if (!/^\d{17}[\dXx]$/.test(String(this.form.idNumber))) {
          this.noticeMessage = '请输入有效的18位身份证号码'
          this.noticeType = 'warning'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }

        this.submitting = true
        const fd = new FormData()
        // 字段名映射：前端 camelCase -> 后端 snake_case
        const payloadMap = {
          realName: 'real_name',
          age: 'age',
          phone: 'phone',
          email: 'email',
          idNumber: 'id_number',
          gender: 'gender',
          ethnicity: 'ethnicity',
          political: 'political',
          birthDate: 'birth_date',
          education: 'education',
          degree: 'degree',
          school: 'school',
          major: 'major',
          company: 'company',
          workExperience: 'work_experience',
          position: 'position',
        }
        Object.entries(this.form).forEach(([k, v]) => {
          const serverKey = payloadMap[k] || k
          const val = v === undefined || v === null ? '' : String(v)
          // 可选字段为空时不提交，避免后端将空字符串判为非法
          const optionalKeys = ['gender', 'ethnicity', 'political', 'birthDate', 'education', 'degree', 'school', 'major', 'company', 'age']
          const isOptional = optionalKeys.includes(k)
          if (isOptional && !val.trim()) return
          // birthDate 格式化为 YYYY-MM-DD
          if (k === 'birthDate') {
            const d = String(v || '').trim()
            if (!d) return
            // 浏览器原生 date input 返回已是 YYYY-MM-DD
            fd.append(serverKey, d)
            return
          }
          fd.append(serverKey, val)
        })
        // 多文件：显式携带文件名，避免某些环境下默认名称为 blob
        // 证件照片（优先追加，带标签化文件名）
        if (this.personalPhoto) fd.append('attachments', this.personalPhoto, `personal_photo_${this.personalPhoto.name}`)
        if (this.idCardFront) fd.append('attachments', this.idCardFront, `id_front_${this.idCardFront.name}`)
        if (this.idCardBack) fd.append('attachments', this.idCardBack, `id_back_${this.idCardBack.name}`)
        if (this.graduationCert) fd.append('attachments', this.graduationCert, `graduation_cert_${this.graduationCert.name}`)
        // 其他补充材料
        this.attachments.forEach(f => fd.append('attachments', f, f.name))

        const startStr = this.exam && this.exam.enroll_start_time
        const endStr = this.exam && this.exam.enroll_end_time
        const now = new Date()
        const start = startStr ? new Date(startStr) : null
        const end = endStr ? new Date(endStr) : null
        if ((start && now < start) || (end && now > end)) {
          this.noticeMessage = '报名时间已结束，无法进行报名'
          this.noticeType = 'warning'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }

        // 需要当前考试ID
        const examId = this.exam?.id
        if (!examId) {
          this.noticeMessage = '缺少考试ID，无法提交报名'
          this.noticeType = 'error'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
          return
        }

        // 携带令牌
        const token = localStorage.getItem('user_access_token') || ''
        const res = await fetch(`/api/exam/exams/${examId}/apply/`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: fd
        })
        if (!res.ok) {
          // 优先读取 JSON 错误信息
          const ct = res.headers.get('content-type') || ''
          let msg = `HTTP ${res.status}`
          if (ct.includes('application/json')) {
            const err = await res.json().catch(() => null)
            if (err) {
              if (typeof err === 'object' && !Array.isArray(err)) {
                // 将字段错误合并为友好提示
                const parts = []
                // 需要隐藏字段名的key
                const hideFieldKeys = ['non_field_errors', 'detail', 'error']
                // 字段名中英文映射
                const fieldNameMap = {
                  'name': '姓名',
                  'phone': '手机号',
                  'email': '邮箱',
                  'id_card': '身份证号',
                  'exam': '考试'
                }
                Object.entries(err).forEach(([field, detail]) => {
                  const first = Array.isArray(detail) ? detail[0] : detail
                  const text = typeof first === 'string' ? first : (first?.message || JSON.stringify(first))
                  // 对于通用错误字段，直接显示错误内容，不显示字段名
                  if (hideFieldKeys.includes(field)) {
                    parts.push(text)
                  } else {
                    const fieldLabel = fieldNameMap[field] || field
                    parts.push(`${fieldLabel}: ${text}`)
                  }
                })
                msg = parts.length ? parts.join('\n') : msg
              } else if (err.error) {
                msg = err.error
              }
            }
          } else {
            const text = await res.text().catch(() => '')
            if (text) msg = text
          }
          throw new Error(msg)
        }
        await res.json()
        this.noticeMessage = '报名资料提交成功！'
        this.noticeType = 'success'
        this.noticeActions = []
        this.noticeVisible = true
        setTimeout(() => { this.noticeVisible = false }, 2500)
        this.$emit('submitted')
      } catch (e) {
        console.error(e)
        try {
          const startStr = this.exam && this.exam.enroll_start_time
          const endStr = this.exam && this.exam.enroll_end_time
          const now = new Date()
          const start = startStr ? new Date(startStr) : null
          const end = endStr ? new Date(endStr) : null
          if ((start && now < start) || (end && now > end)) {
            this.noticeMessage = '报名时间已结束，无法进行报名'
            this.noticeType = 'warning'
            this.noticeActions = []
            this.noticeVisible = true
            setTimeout(() => { this.noticeVisible = false }, 2500)
          } else {
            this.noticeMessage = `提交失败：${e?.message || '请稍后重试'}`
            this.noticeType = 'error'
            this.noticeActions = []
            this.noticeVisible = true
            setTimeout(() => { this.noticeVisible = false }, 2500)
          }
        } catch (_) {
          this.noticeMessage = `提交失败：${e?.message || '请稍后重试'}`
          this.noticeType = 'error'
          this.noticeActions = []
          this.noticeVisible = true
          setTimeout(() => { this.noticeVisible = false }, 2500)
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
/* ========== 主容器 ========== */
.exam-registration {
  background: #fff;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
}

/* ========== 表单容器 ========== */
.sheet-form {
  width: 100%;
}

/* ========== 标题样式 ========== */
.sheet-title {
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px;
  padding-bottom: 16px;
  border-bottom: 2px solid #3b82f6;
}

.sheet-subtitle {
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

.upload-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 32px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #3b82f6;
  text-align: center;
}

/* ========== 预填充提示 ========== */
.prefill-note {
  margin: 0 0 20px;
  padding: 12px 16px;
  font-size: 14px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  text-align: center;
}

.prefill-note.reject-note {
  color: #991b1b;
  background: #fee2e2;
  border-color: #fca5a5;
}

/* ========== 表格样式 ========== */
.sheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
  border: 1px solid #e5e7eb;
}

.sheet-table td {
  border: 1px solid #e5e7eb;
  padding: 12px 16px;
  vertical-align: middle;
  text-align: left;
}

/* 分组标题列 - 蓝色竖排 */
.sheet-table .th {
  background: #3b82f6;
  color: #fff;
  font-weight: 600;
  text-align: center;
  width: 70px;
  min-width: 70px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 3px;
}

/* 字段名列 - 统一宽度和对齐 */
.sheet-table td:not(.th):first-of-type,
.sheet-table tr td:nth-child(2):not(.th) {
  width: 90px;
  min-width: 90px;
  text-align: right;
  font-weight: 500;
  color: #374151;
  background: #f9fafb;
  padding-right: 16px;
  white-space: nowrap;
}

/* 内容列 */
.sheet-table td[colspan] {
  background: #fff;
  text-align: left;
}

/* ========== 输入框样式 ========== */
.sheet-form input[type="text"],
.sheet-form input[type="tel"],
.sheet-form input[type="number"],
.sheet-form select,
.sheet-form textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  color: #1f2937;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.sheet-form input:focus,
.sheet-form select:focus,
.sheet-form textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.sheet-form select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L2 4h8z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

.sheet-form textarea {
  min-height: 80px;
  resize: vertical;
}

/* 查看模式文本 */
.sheet-table td span {
  display: block;
  color: #1f2937;
  padding: 8px 0;
}

/* ========== 邮箱输入 ========== */
.email-input-wrapper {
  width: 100%;
}

.email-input {
  width: 100%;
  margin-bottom: 8px;
}

.email-quick-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-domain-btn {
  padding: 4px 10px;
  font-size: 12px;
  color: #3b82f6;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-domain-btn:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

/* ========== 工作经历 ========== */
.work-experience-display {
  padding: 8px 0;
}

.experience-line {
  margin: 4px 0;
  color: #374151;
}

/* ========== 上传卡片 ========== */
.upload-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-card:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-placeholder {
  text-align: center;
}

.upload-icon {
  font-size: 28px;
  color: #9ca3af;
  display: block;
  margin-bottom: 4px;
}

.upload-card:hover .upload-icon {
  color: #3b82f6;
}

.upload-text {
  font-size: 13px;
  color: #6b7280;
}

.upload-card:hover .upload-text {
  color: #3b82f6;
}

.preview-image,
.existing-image {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
}

.preview-image img,
.existing-image img {
  max-width: 140px;
  max-height: 100px;
  object-fit: contain;
  border-radius: 4px;
}

.upload-card .remove-btn {
  position: absolute;
  top: -8px;
  right: calc(50% - 78px);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-card .remove-btn:hover {
  background: #dc2626;
}

/* ========== 其他附件 ========== */
.other-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.attachment-item.existing {
  background: #f0fdf4;
  border-color: #86efac;
}

.attachment-item .file-name {
  font-size: 13px;
  color: #374151;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-item .remove-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fee2e2;
  color: #ef4444;
  border: none;
  cursor: pointer;
  margin-left: 8px;
}

.attachment-item .remove-btn:hover {
  background: #ef4444;
  color: #fff;
}

.other-attachments input[type="file"] {
  margin-top: 8px;
  font-size: 13px;
}

/* ========== 查看附件 ========== */
.view-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.view-attachments .attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.view-attachments .download-btn {
  padding: 6px 14px;
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.view-attachments .download-btn:hover {
  background: #2563eb;
}

.view-image {
  text-align: center;
}

.view-image img {
  max-width: 140px;
  max-height: 100px;
  object-fit: contain;
}

/* ========== 按钮样式 ========== */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn-action {
  padding: 12px 36px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: #fff;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* ========== 准考证样式 ========== */
.ticket-view {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  padding: 24px;
}

.ticket-header {
  text-align: center;
  margin-bottom: 24px;
}

.ticket-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 12px;
}

.company-name {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.exam-year {
  font-size: 15px;
  color: #374151;
}

.personal-info {
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
}

.info-item {
  flex: 1;
}

.info-item.full-width {
  flex: 1 0 100%;
}

.info-label {
  font-weight: 600;
  min-width: 90px;
  display: inline-block;
}

.info-value {
  color: #1f2937;
}

.ticket-reminder {
  text-align: center;
  margin-bottom: 24px;
  color: #374151;
}

.ticket-reminder p {
  margin: 4px 0;
}

.examinee-notice {
  margin-top: 32px;
}

.examinee-notice h3 {
  text-align: center;
  font-size: 16px;
  margin-bottom: 12px;
}

.examinee-notice ol {
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.8;
  color: #374151;
}

.ticket-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
}

/* ========== 空状态 ========== */
.empty-state {
  text-align: center;
  padding: 48px;
  color: #6b7280;
}

/* ========== 打印样式 ========== */
@media print {
  .prefill-note,
  .form-actions,
  .ticket-actions,
  .attachment-table,
  .upload-title,
  .upload-card,
  input[type="file"] {
    display: none !important;
  }

  .exam-registration {
    padding: 0 !important;
    max-width: 100% !important;
  }
}
</style>
